import './Tooltip.css'

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import infoIcon from '../imgs/info.svg'

const Tooltip = ({ title, description, isTooltipOpen, setIsTooltipOpen, triggerRef, showTime = 2500 }) => {
  const tooltipRef = useRef(null) // A ref for the tooltip itself
  const [position, setPosition] = useState(null)

  // Auto-hide timer
  useEffect(() => {
    let timerId
    if (isTooltipOpen) {
      timerId = setTimeout(() => {
        setIsTooltipOpen(false)
      }, showTime)
    }
    return () => {
      clearTimeout(timerId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTooltipOpen])

  // This effect calculates the position for the tooltip.
  // useLayoutEffect runs after the DOM is painted, which is ideal for measurements.
  const calculatePosition = useCallback(() => {
    if (triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const { innerWidth: viewportWidth, innerHeight: viewportHeight } = window
      const margin = 8

      let top = triggerRect.top - tooltipRect.height - margin
      let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2

      if (left < margin) {
        left = margin
      } else if (left + tooltipRect.width > viewportWidth - margin) {
        left = viewportWidth - tooltipRect.width - margin
      }

      if (top < margin) {
        top = triggerRect.bottom + margin
        if (top + tooltipRect.height > viewportHeight - margin) {
          top = viewportHeight - tooltipRect.height - margin
        }
      }

      // Convert to absolute position
      const absoluteTop = top + window.scrollY
      const absoluteLeft = left + window.scrollX

      setPosition({ top: absoluteTop, left: absoluteLeft })
    }
  }, [triggerRef]) // Recalculate if the trigger ref itself changes

  // Recalculate position when tooltip opens or window is scrolled/resized
  useLayoutEffect(() => {
    if (isTooltipOpen) {
      calculatePosition() // Initial position

      window.addEventListener('scroll', calculatePosition, true)
      window.addEventListener('resize', calculatePosition)

      return () => {
        window.removeEventListener('scroll', calculatePosition, true)
        window.removeEventListener('resize', calculatePosition)
      }
    }
  }, [isTooltipOpen, calculatePosition])

  if (!isTooltipOpen) {
    return null
  }

  // --- The Portal ---
  // We render the tooltip into document.body to escape the overflow:hidden parent.
  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      className='tooltip-container'
      // Apply the calculated position, or render off-screen initially to be measured.
      style={
        position
          ? {
              position: 'absolute', // Use absolute positioning to scroll with the page
              top: `${position.top}px`,
              left: `${position.left}px`,
              // The z-index and other styles will come from your Tooltip.css
            }
          : {
              position: 'absolute',
              top: '-9999px',
              left: '-9999px',
            }
      }
    >
      <img src={infoIcon} alt='icon' />
      <div>
        <div className='tooltip-title'>{title}</div>
        <div className='tooltip-description'>{description}</div>
      </div>
    </div>,
    document.body // The destination for our portal
  )
}

export default Tooltip
